import { useState, useEffect, useCallback } from 'react';
import { API_BASE, ENDPOINTS } from '../config/api';
import { parseJsonResponse } from '../utils/httpJson';

export type HealthResponse = {
  status: string;
  service?: string;
  version?: string;
  checks?: Record<string, { ok: boolean; detail: string }>;
  latency_ms?: number;
};

const POLL_MS = 60_000;

export function useApiHealth(poll = true) {
  const [data, setData] = useState<HealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const url = API_BASE
      ? `${API_BASE}${ENDPOINTS.readiness}`
      : new URL(ENDPOINTS.readiness, window.location.origin).toString();

    try {
      const t0 = Date.now();
      const res = await fetch(url);
      const latency_ms = Date.now() - t0;
      const parsed = await parseJsonResponse<HealthResponse>(res, { url });
      if (!res.ok) {
        setError(`HTTP ${res.status} - gateway health check failed`);
        setData(null);
        return;
      }
      setData({ ...parsed, latency_ms });
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Health check failed');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    if (!poll) return undefined;
    const id = window.setInterval(refresh, POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh, poll]);

  return { data, loading, error, refresh };
}
